import React, { useState, useEffect, useRef } from 'react';
import {
  Bell,
  Shield,
  Globe,
  Palette,
  Mail,
  Smartphone,
  Save,
  Cloud,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Building2,
  Upload,
  Camera,
  Trash2,
  Phone,
  FileText,
  MessageSquare,
  Headphones,
  Settings2,
  Plus,
  Car,
  Image as ImageIcon,
  Sparkles,
  Percent,
  ChevronDown,
  FileText as FileTextIcon,
  ScrollText,
  Info
} from 'lucide-react';
import { PLANS as PLAN_CONFIG } from '../config/plans';
import PageHeader from '../components/layout/PageHeader';
import Card, { CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useAuth } from '../context/AuthContext';
import { useSettings, colorThemes, daysFilterOptions } from '../context/SettingsContext';
import { userAPI, platformAPI } from '../services/api';
import brandingConfig from '../config/appConfig';
import { invalidateVehicleTypesCache } from '../hooks/useVehicleTypes';
import AddressAutocomplete from '../components/maps/AddressAutocomplete';
import { ToastModal } from '../components/ui/Toast';
import ConfirmModal from '../components/ui/ConfirmModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const Settings = () => {
  const { user, travelsInfo, refreshUser } = useAuth();
  const { settings: appSettings, updateSettings: updateAppSettings, loading: settingsLoading } = useSettings();
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);

  // App Preferences State
  const [appPreferences, setAppPreferences] = useState({
    defaultDaysFilter: 15,
    primaryColor: '#3B82F6',
    secondaryColor: '#10B981',
    sidebarColor: '#1F2937',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    currencySymbol: '₹',
    notifications: { email: true, whatsapp: false }
  });
  const [savingAppPreferences, setSavingAppPreferences] = useState(false);
  const [appPreferencesMessage, setAppPreferencesMessage] = useState({ type: '', text: '' });

  // Business Profile State
  const [businessProfile, setBusinessProfile] = useState({
    businessType: 'both',
    name: '',
    phone: '',
    email: '',
    website: '',
    street: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    latitude: null,
    longitude: null,
    gstNumber: '',
    panNumber: '',
    description: '',
    workingHours: ''
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [coverPreview, setCoverPreview] = useState(brandingConfig.defaultTravelsCover || null);
  const [coverFile, setCoverFile] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [confirmBizType, setConfirmBizType] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });

  const [settings, setSettings] = useState({
    // Notification Settings
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,

    // App Settings
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    language: 'en',
    dateFormat: 'DD/MM/YYYY',

    // Security Settings
    twoFactorAuth: false,
    sessionTimeout: 30,
    passwordExpiry: 90,

    // Cloudinary Settings
    cloudinaryCloudName: '',
    cloudinaryApiKey: '',
    cloudinaryApiSecret: ''
  });

  const [saved, setSaved] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'success' });

  const [lastSaved, setLastSaved] = useState({});

  const formatSavedTime = (date) => {
    if (!date) return null;
    return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const showToast = (title, message, type = 'success', section = 'general') => {
    setLastSaved(prev => ({ ...prev, [section]: new Date() }));
    setToast({ isOpen: true, title, message, type });
  };
  const [cloudinaryStatus, setCloudinaryStatus] = useState(null);

  // Platform Settings State (Admin only)
  const isAdmin = user?.role === 'admin';
  const [platformSettings, setPlatformSettings] = useState({
    supportContact: { phone: '', email: '', address: '', workingHours: '' },
    socialLinks: { facebook: '', instagram: '', twitter: '', youtube: '', whatsapp: '' },
    appConfig: { appName: '', tagline: '', brandColor: '#3b82f6', supportEmail: '', supportPhone: '', frontendUrl: '' },
    founderDiscount: { enabled: false, percent: 20, cutoffDate: '', duration: 'lifetime', label: 'Founding member offer' },
    termsAndConditions: '',
    privacyPolicy: '',
    refundPolicy: '',
    aboutUs: '',
    vehicleTypes: []
  });
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [platformMessage, setPlatformMessage] = useState({ type: '', text: '' });
  const [openLegal, setOpenLegal] = useState(null); // which legal section is expanded

  // Settings navigation tabs (differ by role) — one section per tab.
  const settingsTabs = isAdmin
    ? [
        { id: 'platform', label: 'Platform', icon: Settings2 },
        { id: 'vehicleTypes', label: 'Vehicle Types', icon: Car },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'application', label: 'Application', icon: Palette },
        { id: 'security', label: 'Security', icon: Shield },
      ]
    : [
        { id: 'business', label: 'Business Profile', icon: Building2 },
        { id: 'preferences', label: 'App Preferences', icon: Settings2 },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'application', label: 'Application', icon: Palette },
        { id: 'storage', label: 'Image Storage', icon: Cloud },
      ];
  const [activeTab, setActiveTab] = useState(isAdmin ? 'platform' : 'business');
  const currentTab = settingsTabs.some((t) => t.id === activeTab) ? activeTab : settingsTabs[0]?.id;

  // Load travelsInfo into form
  useEffect(() => {
    if (travelsInfo) {
      setBusinessProfile({
        businessType: user?.businessType || 'both',
        name: travelsInfo.name || '',
        phone: travelsInfo.phone || '',
        email: travelsInfo.email || '',
        website: travelsInfo.website || '',
        street: travelsInfo.address?.street || '',
        landmark: travelsInfo.address?.landmark || '',
        city: travelsInfo.address?.city || '',
        state: travelsInfo.address?.state || '',
        pincode: travelsInfo.address?.pincode || '',
        latitude: travelsInfo.address?.coordinates?.latitude || null,
        longitude: travelsInfo.address?.coordinates?.longitude || null,
        gstNumber: travelsInfo.gstNumber || '',
        panNumber: travelsInfo.panNumber || '',
        description: travelsInfo.description || '',
        workingHours: travelsInfo.workingHours || ''
      });
      if (travelsInfo.logo?.url) {
        setLogoPreview(travelsInfo.logo.url);
      }
      // Show the owner's uploaded cover if any; otherwise fall back to the
      // platform-wide default branded cover (configured in appConfig).
      setCoverPreview(travelsInfo.coverImage?.url || brandingConfig.defaultTravelsCover || null);
      if (travelsInfo.updatedAt) {
        setLastSaved(prev => ({ ...prev, businessProfile: new Date(travelsInfo.updatedAt) }));
      }
    }
  }, [travelsInfo]);

  // Load platform settings (admin only)
  useEffect(() => {
    if (isAdmin) {
      platformAPI.getSettings().then(res => {
        if (res.data?.settings) {
          setPlatformSettings(prev => ({
            supportContact: { ...prev.supportContact, ...res.data.settings.supportContact },
            socialLinks: { ...prev.socialLinks, ...res.data.settings.socialLinks },
            appConfig: { ...prev.appConfig, ...res.data.settings.appConfig },
            founderDiscount: {
              ...prev.founderDiscount,
              ...res.data.settings.founderDiscount,
              cutoffDate: res.data.settings.founderDiscount?.cutoffDate
                ? String(res.data.settings.founderDiscount.cutoffDate).slice(0, 10) : ''
            },
            termsAndConditions: res.data.settings.termsAndConditions || '',
            privacyPolicy: res.data.settings.privacyPolicy || '',
            refundPolicy: res.data.settings.refundPolicy || '',
            aboutUs: res.data.settings.aboutUs || '',
            vehicleTypes: res.data.settings.vehicleTypes || []
          }));
          if (res.data.settings.updatedAt) {
            setLastSaved(prev => ({ ...prev, platform: new Date(res.data.settings.updatedAt) }));
          }
        }
      }).catch(err => console.error('Error loading platform settings:', err));
    }
  }, [isAdmin]);

  // Load owner's Cloudinary config
  useEffect(() => {
    if (user?.role === 'owner') {
      userAPI.getCloudinaryConfig().then(res => {
        if (res.data?.config) {
          setSettings(prev => ({
            ...prev,
            cloudinaryCloudName: res.data.config.cloudName || '',
            cloudinaryApiKey: res.data.config.apiKey || '',
            cloudinaryApiSecret: res.data.config.apiSecret || ''
          }));
        }
      }).catch(() => {});
    }
  }, [user?.role]);

  // Load app settings
  useEffect(() => {
    if (appSettings) {
      setAppPreferences({
        defaultDaysFilter: appSettings.defaultDaysFilter || 15,
        primaryColor: appSettings.primaryColor || '#3B82F6',
        secondaryColor: appSettings.secondaryColor || '#10B981',
        sidebarColor: appSettings.sidebarColor || '#1F2937',
        dateFormat: appSettings.dateFormat || 'DD/MM/YYYY',
        timeFormat: appSettings.timeFormat || '12h',
        currencySymbol: appSettings.currencySymbol || '₹',
        notifications: {
          email: appSettings.notifications?.email !== false,
          whatsapp: appSettings.notifications?.whatsapp === true
        }
      });
    }
  }, [appSettings]);

  const handleProfileChange = (field, value) => {
    setBusinessProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleAppPreferenceChange = (field, value) => {
    setAppPreferences(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectTheme = (theme) => {
    setAppPreferences(prev => ({
      ...prev,
      primaryColor: theme.primary,
      secondaryColor: theme.secondary,
      sidebarColor: theme.sidebar
    }));
  };

  const handleSaveAppPreferences = async () => {
    console.log('handleSaveAppPreferences clicked!');
    console.log('appPreferences to save:', appPreferences);
    setSavingAppPreferences(true);
    setAppPreferencesMessage({ type: '', text: '' });

    try {
      const result = await updateAppSettings(appPreferences);
      console.log('Save result:', result);
      if (result.success) {
        setAppPreferencesMessage({ type: 'success', text: 'App preferences saved successfully!' });
        showToast('Saved!', 'App preferences saved successfully.', 'success', 'appPreferences');
      } else {
        setAppPreferencesMessage({ type: 'error', text: result.message || 'Failed to save preferences' });
      }
    } catch (error) {
      console.error('Error saving app preferences:', error);
      setAppPreferencesMessage({ type: 'error', text: 'Failed to save preferences' });
    } finally {
      setSavingAppPreferences(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setProfileMessage({ type: 'error', text: 'Logo size should be less than 2MB' });
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    setUploadingLogo(true);
    setProfileMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      await userAPI.uploadTravelsLogo(formData);
      setProfileMessage({ type: 'success', text: 'Logo uploaded successfully!' });
      showToast('Uploaded!', 'Logo uploaded successfully.', 'success', 'businessProfile');
      setLogoFile(null);
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error uploading logo:', error);
      setProfileMessage({ type: 'error', text: error.response?.data?.message || 'Failed to upload logo' });
      showToast('Error', 'Failed to upload logo.', 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setProfileMessage({ type: 'error', text: 'Cover image should be less than 5MB' });
        return;
      }
      setCoverFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setCoverPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCoverUpload = async () => {
    if (!coverFile) return;
    setUploadingCover(true);
    setProfileMessage({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('cover', coverFile);
      await userAPI.uploadTravelsCover(formData);
      setProfileMessage({ type: 'success', text: 'Cover image uploaded successfully!' });
      showToast('Uploaded!', 'Cover image uploaded successfully.', 'success', 'businessProfile');
      setCoverFile(null);
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error uploading cover image:', error);
      setProfileMessage({ type: 'error', text: error.response?.data?.message || 'Failed to upload cover image' });
      showToast('Error', 'Failed to upload cover image.', 'error');
    } finally {
      setUploadingCover(false);
    }
  };

  // Save is gated by a confirmation when the business type is changing, since
  // that hides/shows whole sections (Bookings, Drivers, Journeys...).
  const handleSaveProfile = () => {
    const current = user?.businessType || 'both';
    if (businessProfile.businessType !== current) {
      setConfirmBizType(true);
      return;
    }
    doSaveProfile();
  };

  const doSaveProfile = async () => {
    setConfirmBizType(false);
    setSavingProfile(true);
    setProfileMessage({ type: '', text: '' });

    try {
      await userAPI.updateTravelsInfo(businessProfile);
      setProfileMessage({ type: 'success', text: 'Business profile updated successfully!' });
      showToast('Saved!', 'Business profile updated successfully.', 'success', 'businessProfile');
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error saving profile:', error);
      setProfileMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save profile' });
      showToast('Error', 'Failed to save profile. Please try again.', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePlatformChange = (section, field, value) => {
    if (section) {
      setPlatformSettings(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setPlatformSettings(prev => ({ ...prev, [field]: value }));
    }
  };

  // ----- Vehicle type management (admin) -----
  const slugifyType = (label) =>
    (label || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const addVehicleType = () => {
    setPlatformSettings(prev => ({
      ...prev,
      vehicleTypes: [...(prev.vehicleTypes || []), { value: '', label: '', icon: '🚘', category: 'four-wheeler', seats: 4, isActive: true }]
    }));
  };

  const updateVehicleType = (index, field, value) => {
    setPlatformSettings(prev => {
      const list = [...(prev.vehicleTypes || [])];
      const item = { ...list[index], [field]: value };
      // Auto-derive the value slug from the label until the user has typed a custom value
      if (field === 'label' && (!list[index].value || list[index].value === slugifyType(list[index].label))) {
        item.value = slugifyType(value);
      }
      list[index] = item;
      return { ...prev, vehicleTypes: list };
    });
  };

  const removeVehicleType = (index) => {
    setPlatformSettings(prev => ({
      ...prev,
      vehicleTypes: (prev.vehicleTypes || []).filter((_, i) => i !== index)
    }));
  };

  const handleSavePlatform = async () => {
    setSavingPlatform(true);
    setPlatformMessage({ type: '', text: '' });
    // Guard: every vehicle type needs a value and label
    const badType = (platformSettings.vehicleTypes || []).find(t => !t.value?.trim() || !t.label?.trim());
    if (badType) {
      setSavingPlatform(false);
      setPlatformMessage({ type: 'error', text: 'Every vehicle type needs a name. Please fill or remove empty rows.' });
      showToast('Error', 'Every vehicle type needs a name.', 'error');
      return;
    }
    try {
      // Empty date string can't cast to a Mongoose Date — send null instead.
      const payload = {
        ...platformSettings,
        founderDiscount: {
          ...platformSettings.founderDiscount,
          cutoffDate: platformSettings.founderDiscount?.cutoffDate || null
        }
      };
      await platformAPI.updateSettings(payload);
      invalidateVehicleTypesCache(); // so all pages pick up the new list on next mount
      setPlatformMessage({ type: 'success', text: 'Platform settings saved successfully!' });
      showToast('Saved!', 'Platform settings saved successfully.', 'success', 'platform');
    } catch (error) {
      console.error('Error saving platform settings:', error);
      setPlatformMessage({ type: 'error', text: 'Failed to save platform settings' });
      showToast('Error', 'Failed to save platform settings.', 'error');
    } finally {
      setSavingPlatform(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    // Show instant feedback for toggle changes
    if (typeof value === 'boolean') {
      const labels = {
        emailNotifications: 'Email Notifications',
        pushNotifications: 'Push Notifications',
      };
      if (labels[field]) {
        showToast(value ? 'Enabled' : 'Disabled', `${labels[field]} ${value ? 'enabled' : 'disabled'}.`, 'success', 'notifications');
      }
    }
  };

  const handleSave = () => {
    setSaved(true);
    showToast('Saved!', 'Settings saved successfully.');
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="Manage your application settings and preferences"
      />

      {/* Section tabs */}
      <div className="flex gap-1 overflow-x-auto p-1 bg-gray-100 rounded-xl">
        {settingsTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
              currentTab === id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {saved && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
          Settings saved successfully!
        </div>
      )}

      {/* Platform Settings - Admin Only */}
      {isAdmin && currentTab === 'platform' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Headphones className="h-5 w-5 text-gray-600 mr-2" />
                <CardTitle>Platform Settings</CardTitle>
              </div>
              {lastSaved.platform && (
                <span className="text-xs text-green-600 font-medium">Updated: {formatSavedTime(lastSaved.platform)}</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Configure support contact, policies, and social links displayed across the platform
            </p>
          </CardHeader>
          <CardContent>
            {platformMessage.text && (
              <div className={`mb-4 p-4 rounded-lg flex items-center ${
                platformMessage.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                {platformMessage.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 mr-2" />
                ) : (
                  <XCircle className="h-5 w-5 mr-2" />
                )}
                {platformMessage.text}
              </div>
            )}

            {/* Founder / existing-owner discount — premium card */}
            {(() => {
              const fd = platformSettings.founderDiscount;
              const pct = Number(fd.percent) || 0;
              const off = (n) => Math.round(n * (1 - pct / 100));
              const preview = ['Basic', 'Professional'].map((id) => ({ id, base: PLAN_CONFIG[id].monthly, disc: off(PLAN_CONFIG[id].monthly) }));
              return (
                <div className="mb-8">
                  <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                    {/* Header with toggle */}
                    <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3 text-white">
                        <span className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Sparkles className="h-5 w-5" /></span>
                        <div>
                          <div className="font-bold leading-tight">Existing-Owner Discount</div>
                          <div className="text-xs text-white/80">Reward early owners with a % off paid plans</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handlePlatformChange('founderDiscount', 'enabled', !fd.enabled)}
                        className={`relative w-12 h-7 rounded-full transition-colors ${fd.enabled ? 'bg-white' : 'bg-white/30'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full transition-transform ${fd.enabled ? 'translate-x-5 bg-orange-500' : 'bg-white'}`} />
                      </button>
                    </div>

                    <div className={`p-5 transition-opacity ${fd.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
                          <div className="relative">
                            <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input type="number" min="0" max="90" value={fd.percent}
                              onChange={(e) => handlePlatformChange('founderDiscount', 'percent', Number(e.target.value))}
                              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                          <select value={fd.duration}
                            onChange={(e) => handlePlatformChange('founderDiscount', 'duration', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent">
                            <option value="lifetime">For life</option>
                            <option value="first-year">First year only</option>
                            <option value="first-payment">First payment only</option>
                          </select>
                        </div>
                        <Input label="Eligible if joined on/before" type="date"
                          value={fd.cutoffDate || ''}
                          onChange={(e) => handlePlatformChange('founderDiscount', 'cutoffDate', e.target.value)} />
                        <Input label="Badge label" placeholder="Founding member offer"
                          value={fd.label}
                          onChange={(e) => handlePlatformChange('founderDiscount', 'label', e.target.value)} />
                      </div>

                      {/* Live preview */}
                      <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 p-4">
                        <div className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Live preview at {pct}% off</div>
                        <div className="flex flex-wrap gap-4">
                          {preview.map((p) => (
                            <div key={p.id} className="text-sm">
                              <span className="text-gray-500">{p.id}: </span>
                              <span className="line-through text-gray-400">₹{p.base}</span>
                              <span className="font-bold text-gray-900 ml-1.5">₹{p.disc}</span>
                              <span className="text-gray-400">/mo</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <p className="text-xs text-gray-400 mt-3">
                        Leave the date empty to apply to <strong>all existing owners</strong>. Eligible owners see these prices on their Subscription page; the public Pricing page shows the offer to everyone. Billing is manual — charge the shown amount when activating.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* App Config */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Settings2 className="h-4 w-4 mr-2" />
                App Configuration
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="App Name"
                  placeholder="Wheels4Rent"
                  value={platformSettings.appConfig.appName}
                  onChange={(e) => handlePlatformChange('appConfig', 'appName', e.target.value)}
                />
                <Input
                  label="Tagline"
                  placeholder="Buy, Sell & Rent Vehicles"
                  value={platformSettings.appConfig.tagline}
                  onChange={(e) => handlePlatformChange('appConfig', 'tagline', e.target.value)}
                />
                <Input
                  label="Support Email"
                  placeholder="support@example.com"
                  value={platformSettings.appConfig.supportEmail}
                  onChange={(e) => handlePlatformChange('appConfig', 'supportEmail', e.target.value)}
                />
                <Input
                  label="Support Phone"
                  placeholder="+919876543210"
                  value={platformSettings.appConfig.supportPhone}
                  onChange={(e) => handlePlatformChange('appConfig', 'supportPhone', e.target.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand Color (Emails)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={platformSettings.appConfig.brandColor || '#3b82f6'}
                      onChange={(e) => handlePlatformChange('appConfig', 'brandColor', e.target.value)}
                      className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-500">{platformSettings.appConfig.brandColor || '#3b82f6'}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Used in email headers, buttons, and links</p>
                </div>
                <Input
                  label="Frontend URL"
                  placeholder="https://yourdomain.com"
                  value={platformSettings.appConfig.frontendUrl}
                  onChange={(e) => handlePlatformChange('appConfig', 'frontendUrl', e.target.value)}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">These values are used in email templates, notifications, and branding across the app.</p>
            </div>

            {/* Support Contact */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Phone className="h-4 w-4 mr-2" />
                Support Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Phone"
                  placeholder="+91 9876543210"
                  value={platformSettings.supportContact.phone}
                  onChange={(e) => handlePlatformChange('supportContact', 'phone', e.target.value)}
                />
                <Input
                  label="Email"
                  type="email"
                  placeholder="support@wheels4rent.in"
                  value={platformSettings.supportContact.email}
                  onChange={(e) => handlePlatformChange('supportContact', 'email', e.target.value)}
                />
                <Input
                  label="Address"
                  placeholder="123 Main Road, City, State"
                  value={platformSettings.supportContact.address}
                  onChange={(e) => handlePlatformChange('supportContact', 'address', e.target.value)}
                />
                <Input
                  label="Working Hours"
                  placeholder="Mon-Sat, 9:00 AM - 6:00 PM"
                  value={platformSettings.supportContact.workingHours}
                  onChange={(e) => handlePlatformChange('supportContact', 'workingHours', e.target.value)}
                />
              </div>
            </div>

            {/* Social Links */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <MessageSquare className="h-4 w-4 mr-2" />
                Social Links
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Facebook"
                  placeholder="https://facebook.com/yourpage"
                  value={platformSettings.socialLinks.facebook}
                  onChange={(e) => handlePlatformChange('socialLinks', 'facebook', e.target.value)}
                />
                <Input
                  label="Instagram"
                  placeholder="https://instagram.com/yourpage"
                  value={platformSettings.socialLinks.instagram}
                  onChange={(e) => handlePlatformChange('socialLinks', 'instagram', e.target.value)}
                />
                <Input
                  label="Twitter"
                  placeholder="https://twitter.com/yourpage"
                  value={platformSettings.socialLinks.twitter}
                  onChange={(e) => handlePlatformChange('socialLinks', 'twitter', e.target.value)}
                />
                <Input
                  label="YouTube"
                  placeholder="https://youtube.com/yourchannel"
                  value={platformSettings.socialLinks.youtube}
                  onChange={(e) => handlePlatformChange('socialLinks', 'youtube', e.target.value)}
                />
                <Input
                  label="WhatsApp"
                  placeholder="+91 9876543210"
                  value={platformSettings.socialLinks.whatsapp}
                  onChange={(e) => handlePlatformChange('socialLinks', 'whatsapp', e.target.value)}
                />
              </div>
            </div>

            {/* Legal & content pages — accordion (collapsed by default) */}
            <div className="mb-8">
              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                <ScrollText className="h-4 w-4 mr-2 text-gray-400" /> Legal & Content Pages
              </h4>
              <div className="space-y-2.5">
                {[
                  { key: 'aboutUs', label: 'About Us', Icon: Info, placeholder: 'Tell visitors about your platform...' },
                  { key: 'termsAndConditions', label: 'Terms & Conditions', Icon: ScrollText, placeholder: 'Enter your terms and conditions...' },
                  { key: 'privacyPolicy', label: 'Privacy Policy', Icon: Shield, placeholder: 'Enter your privacy policy...' },
                  { key: 'refundPolicy', label: 'Refund Policy', Icon: FileTextIcon, placeholder: 'Enter your refund/cancellation policy...' },
                ].map(({ key, label, Icon, placeholder }) => {
                  const open = openLegal === key;
                  const hasContent = !!(platformSettings[key] && platformSettings[key].replace(/<[^>]*>/g, '').trim());
                  return (
                    <div key={key} className={`border rounded-xl overflow-hidden transition-colors ${open ? 'border-blue-300 shadow-sm' : 'border-gray-200'}`}>
                      <button type="button" onClick={() => setOpenLegal(open ? null : key)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                        <span className="flex items-center gap-2.5 text-sm font-medium text-gray-700">
                          <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${open ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}><Icon className="h-4 w-4" /></span>
                          {label}
                          {hasContent
                            ? <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">Set</span>
                            : <span className="text-[10px] font-semibold text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">Empty</span>}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                      </button>
                      {open && (
                        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                          <ReactQuill
                            theme="snow"
                            value={platformSettings[key]}
                            onChange={(val) => handlePlatformChange(null, key, val)}
                            placeholder={placeholder}
                            modules={{ toolbar: [
                              [{ header: [1, 2, 3, false] }],
                              ['bold', 'italic', 'underline'],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              ['link'],
                              ['clean']
                            ]}}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={handleSavePlatform} disabled={savingPlatform}>
                <Save className="h-4 w-4 mr-2" />
                {savingPlatform ? 'Saving...' : 'Save Platform Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vehicle Types — Admin only, own tab */}
      {isAdmin && currentTab === 'vehicleTypes' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Car className="h-5 w-5 text-gray-600 mr-2" />
                <CardTitle>Vehicle Types</CardTitle>
              </div>
              <button
                type="button"
                onClick={addVehicleType}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg"
              >
                <Plus className="h-3.5 w-3.5" /> Add Type
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500 mb-4">
              These appear across the app (Add Vehicle, Browse, Packages…). Hidden types stay on existing vehicles but won't be offered for new selections.
            </p>
            <div className="space-y-2">
              {/* Header row */}
              <div className="hidden sm:grid grid-cols-12 gap-2 px-1 text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                <div className="col-span-1">Icon</div>
                <div className="col-span-4">Name (label)</div>
                <div className="col-span-3">Code (value)</div>
                <div className="col-span-2">Seats</div>
                <div className="col-span-1 text-center">Show</div>
                <div className="col-span-1"></div>
              </div>

              {(platformSettings.vehicleTypes || []).map((vt, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded-lg p-2">
                  <input
                    value={vt.icon || ''}
                    onChange={(e) => updateVehicleType(idx, 'icon', e.target.value)}
                    className="col-span-2 sm:col-span-1 w-full text-center px-1 py-1.5 border border-gray-200 rounded-md text-sm"
                    placeholder="🚐"
                  />
                  <input
                    value={vt.label || ''}
                    onChange={(e) => updateVehicleType(idx, 'label', e.target.value)}
                    className="col-span-6 sm:col-span-4 w-full px-2 py-1.5 border border-gray-200 rounded-md text-sm"
                    placeholder="e.g., Mini Bus"
                  />
                  <input
                    value={vt.value || ''}
                    onChange={(e) => updateVehicleType(idx, 'value', e.target.value)}
                    className="col-span-4 sm:col-span-3 w-full px-2 py-1.5 border border-gray-200 rounded-md text-sm font-mono text-gray-600"
                    placeholder="mini-bus"
                  />
                  <input
                    type="number" min="1"
                    value={vt.seats ?? ''}
                    onChange={(e) => updateVehicleType(idx, 'seats', Number(e.target.value))}
                    className="col-span-4 sm:col-span-2 w-full px-2 py-1.5 border border-gray-200 rounded-md text-sm"
                    placeholder="Seats"
                  />
                  <label className="col-span-4 sm:col-span-1 flex items-center justify-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={vt.isActive !== false}
                      onChange={(e) => updateVehicleType(idx, 'isActive', e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                  </label>
                  <div className="col-span-4 sm:col-span-1 flex justify-end">
                    <button type="button" onClick={() => removeVehicleType(idx)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-md">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}

              {(platformSettings.vehicleTypes || []).length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">No vehicle types yet. Click "Add Type" to create one.</p>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <Button onClick={handleSavePlatform} disabled={savingPlatform}>
                <Save className="h-4 w-4 mr-2" />
                {savingPlatform ? 'Saving...' : 'Save Vehicle Types'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Business Profile / Travels Info - Only for Owners */}
      {user?.role === 'owner' && currentTab === 'business' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Building2 className="h-5 w-5 text-gray-600 mr-2" />
                <CardTitle>Business Profile</CardTitle>
              </div>
              {lastSaved.businessProfile && (
                <span className="text-xs text-green-600 font-medium">Updated: {formatSavedTime(lastSaved.businessProfile)}</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Your business information displayed to customers
            </p>
          </CardHeader>
          <CardContent>
            {profileMessage.text && (
              <div className={`mb-4 p-4 rounded-lg flex items-center ${
                profileMessage.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                {profileMessage.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 mr-2" />
                ) : (
                  <XCircle className="h-5 w-5 mr-2" />
                )}
                {profileMessage.text}
              </div>
            )}

            {/* Logo Upload Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Business Logo
              </label>
              <div className="flex items-center space-x-6">
                {/* Logo Preview */}
                <div className="relative">
                  <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Building2 className="h-10 w-10 text-gray-400" />
                    )}
                  </div>
                  {logoPreview && (
                    <button
                      type="button"
                      onClick={() => {
                        setLogoPreview(travelsInfo?.logo?.url || null);
                        setLogoFile(null);
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoSelect}
                    accept="image/*"
                    className="hidden"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      {logoPreview ? 'Change Logo' : 'Select Logo'}
                    </Button>
                    {logoFile && (
                      <Button
                        type="button"
                        onClick={handleLogoUpload}
                        disabled={uploadingLogo}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Recommended: Square image, max 2MB (PNG, JPG)
                  </p>
                </div>
              </div>
            </div>

            {/* Cover / Background Image Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Cover / Background Image
              </label>
              {/* Wide preview */}
              <div className="relative w-full h-36 sm:h-44 rounded-xl border-2 border-dashed border-gray-300 overflow-hidden bg-gray-50 flex items-center justify-center">
                {coverPreview ? (
                  <img src={coverPreview} alt="Cover preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-gray-400">
                    <ImageIcon className="h-10 w-10 mx-auto mb-1" />
                    <p className="text-xs">No cover image — your first vehicle photo is used by default</p>
                  </div>
                )}
                {coverPreview && (
                  <button
                    type="button"
                    onClick={() => { setCoverPreview(travelsInfo?.coverImage?.url || null); setCoverFile(null); }}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                )}
              </div>
              <input type="file" ref={coverInputRef} onChange={handleCoverSelect} accept="image/*" className="hidden" />
              <div className="flex flex-wrap gap-2 mt-3">
                <Button type="button" variant="outline" onClick={() => coverInputRef.current?.click()}>
                  <Camera className="h-4 w-4 mr-2" />
                  {coverPreview ? 'Change Cover' : 'Select Cover'}
                </Button>
                {coverFile && (
                  <Button type="button" onClick={handleCoverUpload} disabled={uploadingCover}>
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingCover ? 'Uploading...' : 'Upload Cover'}
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Shown behind your name on the public page. Wide image works best, max 5MB (PNG, JPG)
              </p>
            </div>

            {/* Business type — controls which features/menus you see */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">What does your business do?</label>
              <div className="grid grid-cols-3 gap-2 max-w-md">
                {[
                  { value: 'rental', label: 'Renting', desc: 'Rent out vehicles' },
                  { value: 'sale', label: 'Selling', desc: 'Sell vehicles' },
                  { value: 'both', label: 'Both', desc: 'Rent & sell' },
                ].map(({ value, label, desc }) => (
                  <button
                    type="button"
                    key={value}
                    onClick={() => handleProfileChange('businessType', value)}
                    className={`flex flex-col items-center text-center gap-0.5 p-3 rounded-xl border-2 transition-all ${
                      businessProfile.businessType === value ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className={`text-sm font-semibold ${businessProfile.businessType === value ? 'text-blue-700' : 'text-gray-700'}`}>{label}</span>
                    <span className="text-[11px] text-gray-400 leading-tight">{desc}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">Renting hides sale features; Selling hides rental features (bookings, drivers, journeys). Save to apply.</p>
            </div>

            {/* Business Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Business Name"
                placeholder="Your Travels Name"
                value={businessProfile.name}
                onChange={(e) => handleProfileChange('name', e.target.value)}
              />
              <Input
                label="Business Phone"
                placeholder="9876543210"
                value={businessProfile.phone}
                onChange={(e) => handleProfileChange('phone', e.target.value)}
              />
              <Input
                label="Business Email"
                type="email"
                placeholder="contact@yourtravels.com"
                value={businessProfile.email}
                onChange={(e) => handleProfileChange('email', e.target.value)}
              />
              <Input
                label="Website"
                placeholder="www.yourtravels.com"
                value={businessProfile.website}
                onChange={(e) => handleProfileChange('website', e.target.value)}
              />
            </div>

            {/* Address — Google Places autocomplete fills city/state/pincode automatically */}
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Address</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <AddressAutocomplete
                    label="Street Address"
                    placeholder="Start typing your address..."
                    value={businessProfile.street}
                    onChange={(location) => {
                      handleProfileChange('street', location.address);
                      if (location.city) handleProfileChange('city', location.city);
                      if (location.state) handleProfileChange('state', location.state);
                      if (location.pincode) handleProfileChange('pincode', location.pincode);
                      if (location.lat) handleProfileChange('latitude', location.lat);
                      if (location.lng) handleProfileChange('longitude', location.lng);
                    }}
                    onClear={() => {
                      handleProfileChange('street', '');
                      handleProfileChange('latitude', null);
                      handleProfileChange('longitude', null);
                    }}
                  />
                </div>
                <Input
                  label="Landmark"
                  placeholder="Near Bus Stand"
                  value={businessProfile.landmark}
                  onChange={(e) => handleProfileChange('landmark', e.target.value)}
                />
                <Input
                  label="City"
                  placeholder="Bangalore"
                  value={businessProfile.city}
                  onChange={(e) => handleProfileChange('city', e.target.value)}
                />
                <Input
                  label="State"
                  placeholder="Karnataka"
                  value={businessProfile.state}
                  onChange={(e) => handleProfileChange('state', e.target.value)}
                />
                <Input
                  label="Pincode"
                  placeholder="560001"
                  value={businessProfile.pincode}
                  onChange={(e) => handleProfileChange('pincode', e.target.value)}
                />
              </div>
            </div>

            {/* Tax Details */}
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Tax Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="GST Number"
                  placeholder="22AAAAA0000A1Z5"
                  value={businessProfile.gstNumber}
                  onChange={(e) => handleProfileChange('gstNumber', e.target.value)}
                />
                <Input
                  label="PAN Number"
                  placeholder="AAAAA1234A"
                  value={businessProfile.panNumber}
                  onChange={(e) => handleProfileChange('panNumber', e.target.value)}
                />
              </div>
            </div>

            {/* Description */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Description
              </label>
              <textarea
                rows={3}
                placeholder="Brief description about your travels business..."
                value={businessProfile.description}
                onChange={(e) => handleProfileChange('description', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white resize-none"
              />
            </div>

            {/* Working Hours */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Working Hours
              </label>
              <Input
                placeholder="e.g., Mon–Sun, 6:00 AM – 10:00 PM"
                value={businessProfile.workingHours}
                onChange={(e) => handleProfileChange('workingHours', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Shown to customers on your public page.</p>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex justify-end">
              <Button onClick={handleSaveProfile} disabled={savingProfile}>
                <Save className="h-4 w-4 mr-2" />
                {savingProfile ? 'Saving...' : 'Save Business Profile'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* App Preferences - Only for Owners */}
      {user?.role === 'owner' && currentTab === 'preferences' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Palette className="h-5 w-5 text-gray-600 mr-2" />
                <CardTitle>App Preferences</CardTitle>
              </div>
              {lastSaved.appPreferences && (
                <span className="text-xs text-green-600 font-medium">Updated: {formatSavedTime(lastSaved.appPreferences)}</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Customize your app appearance and default filters
            </p>
          </CardHeader>
          <CardContent>
            {appPreferencesMessage.text && (
              <div className={`mb-4 p-4 rounded-lg flex items-center ${
                appPreferencesMessage.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                {appPreferencesMessage.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 mr-2" />
                ) : (
                  <XCircle className="h-5 w-5 mr-2" />
                )}
                {appPreferencesMessage.text}
              </div>
            )}

            {/* Default Days Filter */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Days Filter
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Set the default date range for all list views (Bookings, Journeys, Reports, etc.)
              </p>
              <select
                className="w-full md:w-64 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                value={appPreferences.defaultDaysFilter}
                onChange={(e) => handleAppPreferenceChange('defaultDaysFilter', parseInt(e.target.value))}
              >
                {daysFilterOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Booking Notifications */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Booking Notifications
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Choose how customers are notified about their bookings (confirmed, trip started/completed, cancelled).
              </p>
              <div className="space-y-3 md:w-96">
                {/* Email */}
                <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300">
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    <p className="text-xs text-gray-500">Send booking updates to the customer's email</p>
                  </div>
                  <div className="relative inline-flex items-center">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={appPreferences.notifications?.email !== false}
                      onChange={(e) => setAppPreferences(prev => ({ ...prev, notifications: { ...prev.notifications, email: e.target.checked } }))}
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                  </div>
                </label>
                {/* WhatsApp */}
                <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300">
                  <div>
                    <p className="font-medium text-gray-900">WhatsApp</p>
                    <p className="text-xs text-gray-500">Send booking updates to the customer's WhatsApp</p>
                  </div>
                  <div className="relative inline-flex items-center">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={appPreferences.notifications?.whatsapp === true}
                      onChange={(e) => setAppPreferences(prev => ({ ...prev, notifications: { ...prev.notifications, whatsapp: e.target.checked } }))}
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-green-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                  </div>
                </label>
              </div>
            </div>

            {/* Color Theme */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color Theme
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Choose a color theme for your app
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {colorThemes.map(theme => (
                  <button
                    key={theme.name}
                    type="button"
                    onClick={() => handleSelectTheme(theme)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      appPreferences.primaryColor === theme.primary
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{ backgroundColor: theme.primary }}
                      />
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{ backgroundColor: theme.secondary }}
                      />
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{ backgroundColor: theme.sidebar }}
                      />
                    </div>
                    <p className="text-sm font-medium text-gray-700">{theme.name}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Colors (Advanced) */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Custom Colors</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Primary Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={appPreferences.primaryColor}
                      onChange={(e) => handleAppPreferenceChange('primaryColor', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <input
                      type="text"
                      value={appPreferences.primaryColor}
                      onChange={(e) => handleAppPreferenceChange('primaryColor', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Secondary Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={appPreferences.secondaryColor}
                      onChange={(e) => handleAppPreferenceChange('secondaryColor', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <input
                      type="text"
                      value={appPreferences.secondaryColor}
                      onChange={(e) => handleAppPreferenceChange('secondaryColor', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Sidebar Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={appPreferences.sidebarColor}
                      onChange={(e) => handleAppPreferenceChange('sidebarColor', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <input
                      type="text"
                      value={appPreferences.sidebarColor}
                      onChange={(e) => handleAppPreferenceChange('sidebarColor', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Date & Time Format */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Date & Time Preferences</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date Format</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    value={appPreferences.dateFormat}
                    onChange={(e) => handleAppPreferenceChange('dateFormat', e.target.value)}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Time Format</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    value={appPreferences.timeFormat}
                    onChange={(e) => handleAppPreferenceChange('timeFormat', e.target.value)}
                  >
                    <option value="12h">12 Hour (AM/PM)</option>
                    <option value="24h">24 Hour</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Currency Symbol</label>
                  <input
                    type="text"
                    value={appPreferences.currencySymbol}
                    onChange={(e) => handleAppPreferenceChange('currencySymbol', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    maxLength={3}
                  />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: appPreferences.sidebarColor }}>
              <p className="text-white text-sm mb-2">Preview: Sidebar Color</p>
              <div className="flex space-x-2">
                <button
                  className="px-4 py-2 rounded-lg text-white text-sm"
                  style={{ backgroundColor: appPreferences.primaryColor }}
                >
                  Primary Button
                </button>
                <button
                  className="px-4 py-2 rounded-lg text-white text-sm"
                  style={{ backgroundColor: appPreferences.secondaryColor }}
                >
                  Secondary Button
                </button>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={handleSaveAppPreferences} disabled={savingAppPreferences}>
                <Save className="h-4 w-4 mr-2" />
                {savingAppPreferences ? 'Saving...' : 'Save App Preferences'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}


      {/* Notification Settings */}
      {currentTab === 'notifications' && (<Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Bell className="h-5 w-5 text-gray-600 mr-2" />
              <CardTitle>Notification Settings</CardTitle>
            </div>
            {lastSaved.notifications && (
              <span className="text-xs text-green-600 font-medium">Updated: {formatSavedTime(lastSaved.notifications)}</span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Email Notifications</p>
                  <p className="text-sm text-gray-500">Receive updates via email</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={settings.emailNotifications}
                  onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Bell className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Push Notifications</p>
                  <p className="text-sm text-gray-500">Receive push notifications</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={settings.pushNotifications}
                  onChange={(e) => handleChange('pushNotifications', e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>)}

      {/* App Settings */}
      {currentTab === 'application' && (<Card>
        <CardHeader>
          <div className="flex items-center">
            <Palette className="h-5 w-5 text-gray-600 mr-2" />
            <CardTitle>Application Settings</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency
              </label>
              <select
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                value={settings.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
              >
                <option value="INR">Indian Rupee (₹)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <select
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                value={settings.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
              >
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="America/New_York">America/New York (EST)</option>
                <option value="Europe/London">Europe/London (GMT)</option>
                <option value="Asia/Dubai">Asia/Dubai (GST)</option>
              </select>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Format
              </label>
              <select
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                value={settings.dateFormat}
                onChange={(e) => handleChange('dateFormat', e.target.value)}
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>)}

      {/* Cloudinary Settings — for owners */}
      {user?.role === 'owner' && currentTab === 'storage' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Cloud className="h-5 w-5 text-gray-600 mr-2" />
                <CardTitle>Image Storage (Cloudinary)</CardTitle>
              </div>
              {lastSaved.cloudinary && (
                <span className="text-xs text-green-600 font-medium">Updated: {formatSavedTime(lastSaved.cloudinary)}</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Optional — use your own Cloudinary account for vehicle images. If not set, default storage is used.
              Get credentials from{' '}
              <a href="https://cloudinary.com/console" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                cloudinary.com/console
              </a>
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Input
                label="Cloud Name"
                placeholder="your-cloud-name"
                value={settings.cloudinaryCloudName}
                onChange={(e) => handleChange('cloudinaryCloudName', e.target.value)}
                autoComplete="off"
                name="cloudinary-cloud-name"
              />
              <Input
                label="API Key"
                placeholder="123456789012345"
                value={settings.cloudinaryApiKey}
                onChange={(e) => handleChange('cloudinaryApiKey', e.target.value)}
                autoComplete="off"
                name="cloudinary-api-key"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">API Secret</label>
                <div className="relative">
                  <input
                    type={showApiSecret ? 'text' : 'password'}
                    placeholder="Enter your API secret"
                    value={settings.cloudinaryApiSecret}
                    onChange={(e) => handleChange('cloudinaryApiSecret', e.target.value)}
                    autoComplete="new-password"
                    name="cloudinary-api-secret"
                    className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiSecret(!showApiSecret)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showApiSecret ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      const { userAPI } = require('../services/api');
                      await userAPI.updateCloudinaryConfig({
                        cloudName: settings.cloudinaryCloudName,
                        apiKey: settings.cloudinaryApiKey,
                        apiSecret: settings.cloudinaryApiSecret
                      });
                      setProfileMessage({ type: 'success', text: 'Cloudinary settings saved!' });
                      showToast('Saved!', 'Cloudinary settings saved.', 'success', 'cloudinary');
                    } catch (err) {
                      setProfileMessage({ type: 'error', text: 'Failed to save Cloudinary settings' });
                    }
                  }}
                  disabled={!settings.cloudinaryCloudName || !settings.cloudinaryApiKey || !settings.cloudinaryApiSecret}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Cloudinary Config
                </Button>
                {settings.cloudinaryCloudName && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      handleChange('cloudinaryCloudName', '');
                      handleChange('cloudinaryApiKey', '');
                      handleChange('cloudinaryApiSecret', '');
                      try {
                        const { userAPI } = require('../services/api');
                        await userAPI.updateCloudinaryConfig({ cloudName: '', apiKey: '', apiSecret: '' });
                        setProfileMessage({ type: 'success', text: 'Cloudinary config removed. Using default storage.' });
                        showToast('Removed', 'Cloudinary config removed. Using default storage.', 'success', 'cloudinary');
                      } catch (err) {}
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Use Default
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-400">Leave empty to use the platform's default Cloudinary account for image uploads.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Security Settings — Admin only */}
      {user?.role === 'admin' && currentTab === 'security' && <Card>
        <CardHeader>
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-gray-600 mr-2" />
            <CardTitle>Security Settings</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Two-Factor Authentication</p>
                <p className="text-sm text-gray-500">Add an extra layer of security</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={settings.twoFactorAuth}
                  onChange={(e) => handleChange('twoFactorAuth', e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Session Timeout (minutes)"
                type="number"
                value={settings.sessionTimeout}
                onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value))}
              />
              <Input
                label="Password Expiry (days)"
                type="number"
                value={settings.passwordExpiry}
                onChange={(e) => handleChange('passwordExpiry', parseInt(e.target.value))}
              />
            </div>
          </div>
        </CardContent>
      </Card>}


      {/* Save Button — for the tabs whose cards don't carry their own save */}
      {['notifications', 'application'].includes(currentTab) && (
        <div className="flex justify-end">
          <Button variant="primary" onClick={handleSave}>
            <Save className="h-5 w-5 mr-2" />
            Save Settings
          </Button>
        </div>
      )}

      {/* Toast Modal */}
      <ToastModal
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />

      <ConfirmModal
        isOpen={confirmBizType}
        onClose={() => setConfirmBizType(false)}
        onConfirm={doSaveProfile}
        type="warning"
        title="Change business type?"
        message={`Switching to "${{ rental: 'Renting', sale: 'Selling', both: 'Both' }[businessProfile.businessType]}" changes which sections you see — ${businessProfile.businessType === 'sale' ? 'rental features like Bookings, Journeys and Drivers will be hidden.' : businessProfile.businessType === 'rental' ? 'sale/listing features will be hidden.' : 'all rental and sale features will be available.'} Continue?`}
        confirmText="Yes, change it"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Settings;
